import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useButtonPressedCallback,
  useLinkPressedCallback
} from '@zenbusiness/application-commons-clickstream';
import delay from '@zenbusiness/application-commons-utils/delay';
import { WebView } from '@zenbusiness/application-commons-webview';
import Heading from '@zenbusiness/zui/Heading';
import Link from '@zenbusiness/zui/Link';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import AstraAuthorizationModal from '#client/components/AstraAuthorizationModal';
import { FocusGrid } from '#client/components/Grid';
import InstantDepositModal from '#client/components/MoveMoney/InstantDepositModal';
import {
  SETTING_UP_ACCOUNT,
  TRY_ADD_CARD_AGAIN_STEP
} from '#client/components/MoveMoney/InstantDepositModal/constants/instants';
import RenderIf from '#client/components/RenderIf';
import ScanToDownloadAppModal from '#client/components/ScanToDownloadAppModal';
import { ASTRA_STATUS } from '#client/constants/astra';
import { OWNER_CONTRIBUTIONS_URL } from '#client/constants/FAQs';
import { MODAL_NAMES } from '#client/constants/modals';
import { WHICH_ACCOUNT_STEP } from '#client/constants/transfer';
import useAddBankOrDeposit from '#client/hooks/useAddBankOrDeposit';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useCheckDepositModal from '#client/hooks/useCheckDepositModal';
import useInstantBankingCard from '#client/hooks/useInstantBankingCard';
import useMobileCheckDeposit from '#client/hooks/useMobileCheckDeposit';
import useModalContext from '#client/hooks/useModalContext';
import useOpenAstraModal from '#client/hooks/useOpenAstraModal';
import { checkIsMobile } from '#client/utils/devices';

import HelpAnotherWayModal from './HelpAnotherWayModal';
import MoveMoneyOption from './MoveMoneyOption';
import { Wrapper } from './NotFundedOptions.styles';
import OptionSkeleton from './OptionSkeleton';

export default function NotFundedOptions() {
  const { isWebView } = WebView;
  const { getAndRedirectAstraUrl, loading: loadingGetAstraLink } = useOpenAstraModal();
  const { openModal, closeModal } = useModalContext();
  const openCheckDepositModal = useCheckDepositModal();

  const {
    cardList,
    accountStatus,
    hasAcceptedTerms,
    loading: loadingAstraProfile,
    isNeedCreateDestinationCard,
    refreshAstraProfile,
    isHideInstantDepositOption,
    shouldAttemptCreateAstraProfile
  } = useAstraProfileContext();

  const {
    isEnableMobileCheckDeposit,
    loading: loadingCustomerToken,
    launchCheckDeposit
  } = useMobileCheckDeposit();

  const [firstAstraProfileLoading, setFirstAstraProfileLoading] = useState(true);

  /**
   * Use this effect for checking loading skeleton only 1 time
   */

  useEffect(() => {
    if (!loadingAstraProfile) {
      setFirstAstraProfileLoading(false);
    }
  }, [loadingAstraProfile]);

  const { createBankingCardForInstantFunding, loading: createCardLoading } =
    useInstantBankingCard();

  const { openAddBankOrDeposit, loading: loadingCounterPartyPlaid } = useAddBankOrDeposit();

  const handleInstantDeposit = useCallback(async () => {
    if (!hasAcceptedTerms) {
      openModal({
        component: AstraAuthorizationModal
      });
      return;
    }

    if (isNeedCreateDestinationCard) {
      const { error: createCardError } = await createBankingCardForInstantFunding();
      if (createCardError) {
        openModal({
          component: InstantDepositModal,
          initStep: TRY_ADD_CARD_AGAIN_STEP
        });

        return;
      }
      // After create destination card successfully, refresh astra profile to get lasted cards
      refreshAstraProfile();
    }

    if (hasAcceptedTerms === true && cardList && cardList.length === 0) {
      getAndRedirectAstraUrl();
      return;
    }

    openModal({
      component: InstantDepositModal,
      initStep: WHICH_ACCOUNT_STEP
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardList, getAndRedirectAstraUrl, hasAcceptedTerms, openModal]);

  /**
   * Triggers the flow for an instant deposit by refreshing the Astra profile,
   * handling the deposit, and then closing the Instant Deposit modal after a delay.
   */
  const triggerInstantDepositFlow = useCallback(async () => {
    // Refresh the Astra profile to ensure it's up to date
    await refreshAstraProfile();
    handleInstantDeposit();
    delay(closeModal(MODAL_NAMES.INSTANT_DEPOSIT_MODAL), 500);
  }, [closeModal, handleInstantDeposit, refreshAstraProfile]);

  const registerInstantFundingAccount = useCallback(async () => {
    openModal({
      component: InstantDepositModal,
      name: MODAL_NAMES.INSTANT_DEPOSIT_MODAL,
      initStep: SETTING_UP_ACCOUNT,
      actionsKit: {
        triggerInstantDepositFlow
      }
    });
  }, [triggerInstantDepositFlow, openModal]);

  const appPageName = isHideInstantDepositOption
    ? 'banking_account_notfunded_dashboard'
    : 'banking_account_not_funded_dashboard_with_astra';

  const trackMakeBankTransferClick = useButtonPressedCallback({
    elementName: 'make_a_bank_transfer',
    elementText: 'Make a bank transfer',
    appPageName
  });
  const trackInstantDepositClick = useButtonPressedCallback({
    elementName: 'SI_create_invoice',
    elementText: 'Instant Deposit',
    appPageName
  });
  const trackAddFundsClick = useButtonPressedCallback({
    elementName: 'add_funds_another_way',
    elementText: 'Add funds another way',
    appPageName
  });
  const trackMobileClick = useButtonPressedCallback({
    elementName: 'SI_create_invoice',
    elementText: 'Check deposit',
    appPageName
  });
  const moveMoneyOptions = useMemo(
    () => [
      {
        icon: (
          <SharedAsset
            alt=""
            height={16}
            src="/s2/icon/temple-fill.svg"
            width={16}
          />
        ),
        title: 'Make a bank transfer',
        description: 'Link an account to add funds.',
        onClick: () => {
          openAddBankOrDeposit();
          trackMakeBankTransferClick();
        },
        isDisable: Boolean(
          loadingCounterPartyPlaid ||
            loadingGetAstraLink ||
            createCardLoading ||
            loadingCustomerToken
        ),
        isLoading: loadingCounterPartyPlaid
      },
      {
        icon: (
          <SharedAsset
            alt=""
            height={24}
            src="/s2/icon/credit-card-material-outline.svg"
            width={24}
          />
        ),
        title: 'Instant Deposit',
        description: 'Visa® or Mastercard® debit card only.',
        label: 'New',
        onClick: () => {
          trackInstantDepositClick();

          if (shouldAttemptCreateAstraProfile) {
            registerInstantFundingAccount();
          } else {
            handleInstantDeposit();
          }
        },
        isHide: isHideInstantDepositOption,
        isDisable: Boolean(
          loadingCustomerToken ||
            loadingCounterPartyPlaid ||
            loadingGetAstraLink ||
            accountStatus === ASTRA_STATUS.SUSPENDED ||
            loadingAstraProfile ||
            createCardLoading
        ),
        isLoading: loadingGetAstraLink || createCardLoading
      },
      {
        icon: (
          <SharedAsset
            alt=""
            height={16}
            src="/s2/icon/pig-fill.svg"
            width={16}
          />
        ),
        title: 'Add funds another way',
        description: 'Can’t link your bank, or want to deposit from an app?',
        onClick: () => {
          openModal({
            component: HelpAnotherWayModal
          });
          trackAddFundsClick();
        },
        isDisable: Boolean(
          loadingCounterPartyPlaid ||
            loadingGetAstraLink ||
            createCardLoading ||
            loadingCustomerToken
        ),
        isLoading: false
      },
      {
        icon: (
          <SharedAsset
            alt=""
            height={16}
            src="/s2/icon/camera-fill.svg"
            width={16}
          />
        ),
        title: 'Mobile check deposit',
        description: 'Available in our app',
        onClick: () => {
          trackMobileClick();

          if (isEnableMobileCheckDeposit) {
            launchCheckDeposit();
          } else if (checkIsMobile()) {
            openCheckDepositModal({});
          } else {
            openModal({
              component: ScanToDownloadAppModal,
              name: MODAL_NAMES.SCAN_TO_DOWNLOAD_APP_MODAL
            });
          }
        },
        isDisable: Boolean(
          loadingCustomerToken ||
            loadingCounterPartyPlaid ||
            loadingGetAstraLink ||
            createCardLoading
        ),
        isLoading: loadingCustomerToken
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      loadingCounterPartyPlaid,
      isEnableMobileCheckDeposit,
      shouldAttemptCreateAstraProfile,
      isHideInstantDepositOption,
      loadingCustomerToken,
      handleInstantDeposit,
      launchCheckDeposit,
      loadingGetAstraLink,
      accountStatus,
      loadingAstraProfile,
      registerInstantFundingAccount,
      openAddBankOrDeposit,
      openModal,
      createCardLoading
    ]
  );

  const trackLinkClick = useLinkPressedCallback({
    elementName: 'owners_contribution_story',
    elementText: 'See the benefits',
    appPageName: 'banking_notfunded_dashboard',
    destination: OWNER_CONTRIBUTIONS_URL
  });
  const trackLinkPressed = () => trackLinkClick();
  return (
    <>
      <Stack
        gap={{ xs: '8px', md: '16px' }}
        my={{ xs: '40px', md: '48px' }}
        width="100%"
      >
        <Heading
          align="center"
          size="2xl"
          weight="bold"
        >
          Let&apos;s get your bank account ready
        </Heading>
        <Text
          align="center"
          color="secondary"
          size="md"
        >
          Deposit money, then access your Banking dashboard, virtual debit card, and more.
        </Text>
      </Stack>
      <FocusGrid>
        <Wrapper>
          <RenderIf
            fallback={<OptionSkeleton />}
            when={!firstAstraProfileLoading}
          >
            <Stack gap={{ xs: '16px', md: '24px' }}>
              {moveMoneyOptions.map(
                ({ icon, title, description, onClick, isDisable, isLoading, isHide, label }) => (
                  <RenderIf
                    key={title}
                    when={!isHide}
                  >
                    <MoveMoneyOption
                      description={description}
                      icon={icon}
                      isDisable={isDisable}
                      isHide={isHide}
                      isLoading={isLoading}
                      label={label}
                      onClick={onClick}
                      title={title}
                    />
                  </RenderIf>
                )
              )}
            </Stack>
          </RenderIf>
        </Wrapper>
      </FocusGrid>
      <RenderIf when={!isWebView}>
        <Stack
          mt={{ xs: '40px', md: '48px' }}
          width="100%"
        >
          <Text
            align="center"
            size="md"
          >
            Plan on using personal funds?{' '}
            <Link
              color="info"
              href={OWNER_CONTRIBUTIONS_URL}
              onClick={trackLinkPressed}
              rel="noreferrer"
              target="_blank"
              underline="none"
            >
              See the benefits
            </Link>
          </Text>
        </Stack>
      </RenderIf>
    </>
  );
}
